import { ref, watch } from 'vue';
import { defineStore } from 'pinia';

import ApiSnrMultipath from '@/api/ApiSnrMultipath';
import { getAxiosErrorStatus, logError } from '@/api/ApiHelpers';

import MapConsts from '@/components/map/consts/MapConsts';
import { FEATURE_TYPE, MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { MapMode } from '@/components/map/consts/mapFeatureConsts';
import type { GnssStatItem } from '@/components/map/types/gnssStatsTypes';
import { isFeatureDataForType } from '@/components/map/utils/mapFeatureGuards';
import { useMapFeaturesStore } from '@/stores/mapFeaturesStore';
import { useSubNetworksStore } from '@/stores/subNetworksStore';

import type {
	AverageMetricItemRaw,
	AverageMetricResponse,
	ResidualsItemRaw,
	ResidualsResponse,
} from '@/api/ApiTypes';
import type {
	GnssCode,
	GnssTooltipDisplayItem,
	MetricDbItem,
	MetricDbItems,
	MetricFactoryStoreContract,
	Mountpoint,
} from '@/stores/types/mapStoreContracts';


type MetricItemRaw = AverageMetricItemRaw | ResidualsItemRaw;


type MetricGet = (
	handler: (responseData: AverageMetricResponse | ResidualsResponse) => void,
	errorHandler: (error: unknown) => void,
	subNetworkId: number | null,
) => void;


const storeDetails = (
	visibilityObject: MetricDbItem,
	item: MetricItemRaw,
	minValue: number,
	maxValue: number,
	multiplier: number,
	precision: number,
): void => {
	const details: GnssStatItem = {
		valueBright: item.avg_val * multiplier,
		valuePale: null,
		currentDatetimeBright: item.current_datetime,
		currentDatetimePale: null,
		minValue: minValue,
		maxValue: maxValue,
		precision: precision,
	};
	if ('value_is_old' in item) {
		details.valueIsOld = item.value_is_old;
	}
	visibilityObject[item.gnss] = details;
};


const storeBase = (
	name: string,
	apiRequest: MetricGet,
	minValue: number,
	maxValue: number,
	multiplier: number,
	precision: number,
) => defineStore(name, () => {
	const dbItems = ref<MetricDbItems>(new Map());
	const displayItems = ref<GnssTooltipDisplayItem[]>([]);
	const nonNullGnss = ref<GnssCode[]>([]);
	const isVisible = ref(false);

	const featuresStore = useMapFeaturesStore();

	const getDisplayItems = (): GnssTooltipDisplayItem[] => {
		if (!isVisible.value) {
			return [];
		}
		const displayItemsResult: GnssTooltipDisplayItem[] = [];
		for (const feature of featuresStore.features) {
			if (feature.featureType !== FEATURE_TYPE.baseStation) {
				continue;
			}
			if (!isFeatureDataForType(feature.featureData, FEATURE_TYPE.baseStation)) {
				continue;
			}
			const mountpointRaw = feature.featureData.mntpnt_int;
			const mountpoint = typeof mountpointRaw === 'string' ? mountpointRaw : null;
			displayItemsResult.push({
				featureType: feature.featureType,
				featureData: feature.featureData,
				coords: feature.coords,
				stats: mountpoint != null && dbItems.value.has(mountpoint)
					? dbItems.value.get(mountpoint) ?? null
					: null,
			});
		}
		return displayItemsResult;
	};

	const updateDisplayItems = (): void => {
		displayItems.value = getDisplayItems();
	};

	watch(() => featuresStore.features, updateDisplayItems);

	const updateDbItems = (dbItemsValue: MetricItemRaw[]): void => {
		const sortedItems = new Map<Mountpoint, MetricDbItem>();
		const showGnss: GnssCode[] = [];
		for (const item of dbItemsValue) {
			const bucket = sortedItems.get(item.mountpoint) ?? {};
			sortedItems.set(item.mountpoint, bucket);
			storeDetails(bucket, item, minValue, maxValue, multiplier, precision);
			showGnss.push(item.gnss);
		}
		dbItems.value = sortedItems;
		nonNullGnss.value = [...new Set(showGnss)];
		updateDisplayItems();
	};

	const handleGet = (responseData: AverageMetricResponse | ResidualsResponse): void => {
		updateDbItems(responseData.items);
	};

	const handleGetError = (error: unknown): void => {
		if (getAxiosErrorStatus(error) !== 401) {
			logError(error);
		}
	};

	const subNetworksStore = useSubNetworksStore();

	const refresh = (): void => {
		if (isVisible.value) {
			// parameter subNetworkLabel is used only for residuals
			apiRequest(handleGet, handleGetError, subNetworksStore.subNetworkSelectedId);
		}
	};

	watch(
		() => subNetworksStore.subNetworkSelectedId,
		() => {
			refresh();
		},
	);

	const setVisibility = (visibility: boolean): void => {
		isVisible.value = visibility;
		updateDisplayItems();
		refresh();
	};

	return {
		dbItems,
		displayItems,
		nonNullGnss,
		isVisible,
		refresh,
		setVisibility,
	};
});


export const useSnrStore = storeBase(
	'snrStore',
	ApiSnrMultipath.snrGet,
	MapConsts.SNR_MIN,
	MapConsts.SNR_MAX,
	1,
	MapConsts.SNR_PRECISION,
);
export const useMultipathStore = storeBase(
	'multipathStore',
	ApiSnrMultipath.multipathGet,
	MapConsts.MULTIPATH_MIN,
	MapConsts.MULTIPATH_MAX,
	1,
	MapConsts.MULTIPATH_PRECISION,
);
// m -> mm
export const useResidualsStore = storeBase(
	'residualsStore',
	ApiSnrMultipath.residualsGet,
	MapConsts.RESIDUALS_MIN,
	MapConsts.RESIDUALS_MAX,
	1000,
	MapConsts.RESIDUALS_PRECISION,
);

export const factory = (key: MapMode): MetricFactoryStoreContract | null => {
	switch (key) {
	case MAP_MODE.snr:
		return useSnrStore();
	case MAP_MODE.multipath:
		return useMultipathStore();
	case MAP_MODE.residuals:
		return useResidualsStore();
	default:
		return null;
	}
};
